import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useAuthStore } from "../stores/authStore";
import {
  getSubscriptionStatus,
  getSubscription,
  getPlans,
  getPaymentMethods,
  getPaymentHistory,
  getReferralCode,
  getReferralCredits,
  cancelSubscription,
  reactivateSubscription,
  createCheckoutSession,
  createBillingPortalSession,
  verifyCheckoutSession,
  syncSubscription,
  regenerateReferralCode,
  deletePaymentMethod,
  setDefaultPaymentMethod,
  getAgreementStatus,
  createAgreement,
  completeAgreement,
  getActivePromotions,
  getStatusLabel,
  getStatusBadgeClasses,
  formatCents,
  isActiveSubscription,
  type SubscriptionCheckResult,
  type SubscriptionResponse,
  type SubscriptionPlan,
  type PaymentMethod,
  type PaymentHistoryItem,
  type ReferralCode,
  type ReferralCredits,
  type Promotion,
} from "../services/subscriptionService";
import { ConfirmDialog } from "../components/ConfirmDialog";

// =============================================================================
// Helper: days until a date
// =============================================================================
function daysUntil(dateStr: string): number {
  const target = new Date(dateStr);
  const now = new Date();
  const diff = target.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// =============================================================================
// Main Page Component
// =============================================================================

export function SubscriptionPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [status, setStatus] = useState<SubscriptionCheckResult | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [referralCode, setReferralCode] = useState<ReferralCode | null>(null);
  const [referralCredits, setReferralCredits] = useState<ReferralCredits | null>(null);

  // UI state
  const [activeTab, setActiveTab] = useState<"overview" | "billing" | "referrals">("overview");
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [referralInput, setReferralInput] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [activePromotions, setActivePromotions] = useState<Promotion[]>([]);
  const [agreementRequired, setAgreementRequired] = useState(false);
  const [agreementSigned, setAgreementSigned] = useState(false);
  const [signingInProgress, setSigningInProgress] = useState(false);

  // Check URL params for checkout result and E-Sign return
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get("status");
    const agreementParam = params.get("agreement");
    const envelopeId = params.get("envelope_id") || params.get("envelopeId");
    const signingEvent = params.get("event");

    if (checkoutStatus === "success") {
      const sessionId = params.get("session_id");
      window.history.replaceState({}, "", window.location.pathname);

      if (sessionId) {
        // Verify the checkout session with our backend to create the subscription
        setActionSuccess("Verifying payment...");
        verifyCheckoutSession(sessionId)
          .then(() => {
            setActionSuccess("Subscription activated! Welcome to TradeCraft.");
            // Refresh subscription data
            loadData();
          })
          .catch((err) => {
            console.error("Checkout verification failed:", err);
            // Still show success since Stripe processed the payment
            setActionSuccess("Payment received! Your subscription is being activated...");
            // Retry loading after a brief delay
            setTimeout(() => loadData(), 2000);
          });
      } else {
        setActionSuccess("Subscription activated! Welcome to TradeCraft.");
        loadData();
      }
    } else if (checkoutStatus === "canceled") {
      setActionError("Checkout was canceled. If your card was declined, please try a different card — some card issuers block certain online subscriptions. You can try again anytime.");
      window.history.replaceState({}, "", window.location.pathname);
    }

    // Handle return from E-Sign embedded signing
    if (agreementParam === "completed") {
      window.history.replaceState({}, "", window.location.pathname);
      // Normalize: e-sign sends "signed", treat it as "signing_complete"
      const event = signingEvent === "signed" ? "signing_complete" : (signingEvent || "signing_complete");

      // If user canceled or declined, show error
      if (event === "cancel" || event === "decline" || event === "declined") {
        setActionError("Agreement signing was not completed. Please try again.");
        return;
      }

      setSigningInProgress(true);

      // If we have an envelope ID, record the completion on the backend
      const recordCompletion = envelopeId
        ? completeAgreement(envelopeId, event)
        : Promise.resolve({ signed: event === "signing_complete" });

      recordCompletion
        .then(async (result) => {
          if (result.signed || event === "signing_complete") {
            setAgreementSigned(true);
            setActionSuccess("Agreement signed! Redirecting to payment...");
            // Recover referral/promo codes saved before e-sign redirect
            const savedReferral = localStorage.getItem("tc_referral_code") || undefined;
            const savedPromo = localStorage.getItem("tc_promo_code") || undefined;
            localStorage.removeItem("tc_referral_code");
            localStorage.removeItem("tc_promo_code");
            // Automatically proceed to Stripe Checkout
            try {
              const checkoutUrl = await createCheckoutSession(savedReferral, savedPromo);
              window.location.href = checkoutUrl;
            } catch (err) {
              setSigningInProgress(false);
              setActionSuccess(null);
              setActionError(
                err instanceof Error
                  ? `Agreement signed, but checkout failed: ${err.message}. Click Subscribe to try again.`
                  : "Agreement signed, but checkout failed. Click Subscribe to try again."
              );
            }
          } else {
            setSigningInProgress(false);
            setActionError("Agreement signing was not completed. Please try again.");
          }
        })
        .catch(() => {
          // Even if backend recording fails, still proceed to checkout
          // The webhook will handle marking the agreement as signed
          setAgreementSigned(true);
          setActionSuccess("Agreement signed! Redirecting to payment...");
          createCheckoutSession()
            .then((url) => { window.location.href = url; })
            .catch(() => {
              setSigningInProgress(false);
              setActionSuccess(null);
              setActionError("Agreement signed, but checkout failed. Click Subscribe to try again.");
            });
        });
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Sync subscription state from Stripe first (non-blocking)
      try {
        await syncSubscription();
      } catch {
        // Sync is best-effort — continue loading even if it fails
      }

      const [statusResult, subResult, plansResult, methodsResult, historyResult] =
        await Promise.allSettled([
          getSubscriptionStatus(),
          getSubscription(),
          getPlans(),
          getPaymentMethods(),
          getPaymentHistory(),
        ]);

      if (statusResult.status === "fulfilled") setStatus(statusResult.value);
      if (subResult.status === "fulfilled") setSubscription(subResult.value);
      if (plansResult.status === "fulfilled") setPlans(plansResult.value);
      if (methodsResult.status === "fulfilled") setPaymentMethods(methodsResult.value);
      if (historyResult.status === "fulfilled") setPaymentHistory(historyResult.value);

      // Load referral data (non-critical)
      try {
        const [codeResult, creditsResult] = await Promise.all([
          getReferralCode(),
          getReferralCredits(),
        ]);
        setReferralCode(codeResult);
        setReferralCredits(creditsResult);
      } catch {
        // Referral loading is non-critical
      }

      // Check agreement status (non-critical)
      try {
        const agreementResult = await getAgreementStatus();
        setAgreementRequired(agreementResult.required);
        setAgreementSigned(agreementResult.signed);
      } catch {
        // Agreement check is non-critical — if it fails, allow normal flow
      }

      // Load active promotions (non-critical)
      try {
        const promos = await getActivePromotions();
        setActivePromotions(promos);
      } catch {
        // Promotions loading is non-critical
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) loadData();
  }, [loadData, isAuthenticated]);

  // Load promos and plans for unauthenticated visitors too
  useEffect(() => {
    if (!isAuthenticated) {
      getActivePromotions().then(setActivePromotions).catch(() => {});
      getPlans().then(setPlans).catch(() => {});
    }
  }, [isAuthenticated]);

  // Derived state
  const hasSubscription = subscription && subscription.status !== "none";
  const isActive = status?.has_access ?? false;
  const currentPlan = useMemo(
    () => plans.find((p) => p.id === subscription?.plan_id),
    [plans, subscription]
  );

  const renewalInfo = useMemo(() => {
    if (!subscription?.current_period_end) return null;
    const days = daysUntil(subscription.current_period_end);
    const date = formatDate(subscription.current_period_end);
    const isExpiring = subscription.cancel_at_period_end;
    return { days, date, isExpiring };
  }, [subscription]);

  // ── Actions ──

  const handleSubscribe = async () => {
    try {
      setActionLoading(true);
      setActionError(null);

      // Check if agreement is required and not yet signed
      if (agreementRequired && !agreementSigned) {
        // First create the envelope to get the envelope_id
        const baseReturnUrl = `${window.location.origin}/subscription?agreement=completed`;
        const result = await createAgreement(baseReturnUrl);

        if (result.already_signed) {
          // Agreement was already signed — proceed to checkout
          setAgreementSigned(true);
        } else if (result.signing_url) {
          // Persist referral/promo codes before redirecting to e-sign
          if (referralInput) localStorage.setItem("tc_referral_code", referralInput);
          if (promoInput) localStorage.setItem("tc_promo_code", promoInput);
          // Redirect to E-Sign signing ceremony
          window.location.href = result.signing_url;
          return; // Don't clear loading — page is navigating away
        } else {
          setActionError("Failed to start agreement signing. Please try again.");
          setActionLoading(false);
          return;
        }
      }

      // Agreement signed (or not required) — proceed to Stripe checkout
      const url = await createCheckoutSession(referralInput || undefined, promoInput || undefined);
      window.location.href = url;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to start checkout");
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setActionLoading(true);
      setActionError(null);
      const url = await createBillingPortalSession();
      window.location.href = url;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to open billing portal");
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setActionLoading(true);
      setActionError(null);
      await cancelSubscription();
      setActionSuccess("Subscription will be canceled at the end of the current period.");
      setShowCancelDialog(false);
      await loadData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to cancel subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      setActionError(null);
      await reactivateSubscription();
      setActionSuccess("Subscription reactivated successfully!");
      await loadData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to reactivate subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateReferralCode = async () => {
    try {
      setActionLoading(true);
      const newCode = await regenerateReferralCode();
      setReferralCode(newCode);
      setActionSuccess("Referral code regenerated!");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to regenerate referral code");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePaymentMethod = async (methodId: string) => {
    try {
      setActionLoading(true);
      await deletePaymentMethod(methodId);
      setPaymentMethods((prev) => prev.filter((m) => m.id !== methodId));
      setActionSuccess("Payment method removed.");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to delete payment method");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetDefaultPaymentMethod = async (methodId: string) => {
    try {
      setActionLoading(true);
      await setDefaultPaymentMethod(methodId);
      setPaymentMethods((prev) =>
        prev.map((m) => ({ ...m, is_default: m.id === methodId }))
      );
      setActionSuccess("Default payment method updated.");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to update default payment method");
    } finally {
      setActionLoading(false);
    }
  };

  // Clear messages after a delay
  useEffect(() => {
    if (actionSuccess) {
      const timer = setTimeout(() => setActionSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [actionSuccess]);

  useEffect(() => {
    if (actionError) {
      const timer = setTimeout(() => setActionError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [actionError]);

  // ── Render ──

  // ── Unauthenticated: 2-step flow ──
  if (!isAuthenticated) {
    return (
      <div>
        <PageHeader title="Subscription" subtitle="Unlock the full TradeCraft platform" />
        <div className="max-w-4xl mx-auto px-4 pb-10 space-y-6">

          {/* Active Promotion Banner (visible to unauthenticated users) */}
          {activePromotions.length > 0 && (
            <div className="bg-gradient-to-r from-primary-500/15 to-primary-600/10 border border-primary-500/30 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center">
                  <svg className="w-4.5 h-4.5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <span className="text-base font-semibold text-primary-400">Limited Time Offer!</span>
              </div>
              {activePromotions.map((promo) => (
                <div key={promo.id} className="text-sm text-surface-300 ml-11">
                  Use code <span className="font-mono font-bold text-primary-300">{promo.code}</span> for <span className="text-primary-300 font-semibold">{promo.discount_percent}% off</span> your first year
                  {promo.description && <span className="text-surface-400"> — {promo.description}</span>}
                  <div className="text-xs text-surface-500 mt-0.5">
                    Expires {new Date(promo.ends_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 2-Step Process Card */}
          <div className="bg-gradient-to-br from-primary-500/10 via-surface-800/50 to-surface-800/50 border border-primary-500/20 rounded-xl p-8">
            <h2 className="text-xl font-bold text-surface-100 mb-2">Two Simple Steps to Get Started</h2>
            <p className="text-sm text-surface-400 mb-8">
              Subscribing requires an account. Complete these steps in order:
            </p>

            <div className="flex flex-col md:flex-row gap-6">
              {/* Step 1 */}
              <div className="flex-1 relative">
                <div className="bg-surface-800/60 border-2 border-primary-500/40 rounded-xl p-6 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-surface-100">Create Your Account</h3>
                      <p className="text-xs text-surface-400">Free — takes 30 seconds</p>
                    </div>
                  </div>
                  <p className="text-sm text-surface-300 mb-5">
                    Sign up with your email to create your TradeCraft account. No credit card required for this step.
                  </p>
                  <button
                    onClick={() => navigate("/signup")}
                    className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-400 text-white font-semibold rounded-xl text-sm shadow-lg shadow-primary-500/20 transition-all"
                  >
                    Create Free Account
                  </button>
                </div>
                {/* Arrow between steps (desktop) */}
                <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                  <svg className="w-6 h-6 text-primary-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
                  </svg>
                </div>
                {/* Arrow between steps (mobile) */}
                <div className="flex md:hidden justify-center my-2">
                  <svg className="w-6 h-6 text-primary-500 rotate-90" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
                  </svg>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex-1">
                <div className="bg-surface-800/60 border-2 border-surface-600/40 rounded-xl p-6 h-full opacity-75">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-surface-600 text-surface-300 flex items-center justify-center font-bold text-lg flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-surface-300">Subscribe</h3>
                      <p className="text-xs text-surface-500">After creating your account</p>
                    </div>
                  </div>
                  <p className="text-sm text-surface-400 mb-5">
                    Once logged in, sign the subscriber agreement and complete payment via Stripe to unlock all platform features.
                  </p>
                  <div className="w-full px-6 py-3 bg-surface-700/50 border border-surface-600/50 text-surface-500 font-medium rounded-xl text-sm text-center cursor-not-allowed">
                    Available After Sign Up
                  </div>
                </div>
              </div>
            </div>

            <p className="text-center text-sm text-surface-400 mt-6">
              Already have an account?{" "}
              <button onClick={() => navigate("/login")} className="text-primary-400 hover:text-primary-300 font-medium">
                Sign in
              </button>{" "}
              to manage your subscription.
            </p>
          </div>

          {/* Benefits Section — same as authenticated view */}
          <div className="bg-surface-800/40 border border-surface-700/40 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-1">What You Get</h3>
            <p className="text-xs text-surface-500 mb-5">
              Tools and data for your own independent research — TradeCraft is a platform, not a financial advisor.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                {
                  icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                  title: "Unlimited Backtesting",
                  desc: "Run unlimited backtests 24/7 for the duration of your subscription",
                },
                {
                  icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
                  title: "Community Strategies",
                  desc: "Unlimited access to community-shared strategies for research and learning",
                },
                {
                  icon: "M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z",
                  title: "Strategy Builder",
                  desc: "Build and research your own strategies with all NYSE-listed stocks",
                },
                {
                  icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                  title: "Real-Time Market Data",
                  desc: "Live pricing data to better evaluate your backtest runs and research",
                },
                {
                  icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
                  title: "Portfolio Performance",
                  desc: "Daily performance snapshots so you can compare your results against the SPY benchmark",
                },
                {
                  icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
                  title: "Paper & Live Evaluation",
                  desc: "Connect your own Alpaca paper or live account to test and evaluate your strategies independently",
                },
                {
                  icon: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
                  title: "Automated Execution",
                  desc: "Run your strategies automatically in your Alpaca account to validate your own backtests and research",
                },
                {
                  icon: "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4",
                  title: "Full Strategy Control",
                  desc: "Add funds, remove funds, cancel, or liquidate your own strategy allocations at any time",
                },
                {
                  icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                  title: "Backtest vs. Live Comparison",
                  desc: "Compare how your strategies actually perform relative to your backtested expectations",
                },
                {
                  icon: "M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0z",
                  title: "Brokerage Performance",
                  desc: "View your Alpaca paper and live account performance directly in the platform",
                },
                {
                  icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
                  title: "Live Strategy Updates",
                  desc: "Real-time performance updates on your running strategies, visible right in the UI",
                },
              ].map((benefit) => (
                <div key={benefit.title} className="flex items-start gap-3 p-3 rounded-lg bg-surface-700/20 border border-surface-700/30">
                  <div className="w-9 h-9 rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4.5 h-4.5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={benefit.icon} />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-surface-200">{benefit.title}</p>
                    <p className="text-xs text-surface-400 mt-0.5 leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-surface-600 mt-5 leading-relaxed">
              TradeCraft is a self-directed research and execution platform. Nothing on this platform constitutes financial advice, investment recommendations, or solicitation to buy or sell securities. All trading involves risk — past performance does not guarantee future results. You are solely responsible for your own investment decisions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-surface-600 border-t-primary-500 rounded-full animate-spin" />
          <p className="text-surface-400 text-sm">Loading subscription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
          <p className="text-red-400 font-medium">{error}</p>
          <button
            onClick={loadData}
            className="mt-4 px-4 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg text-sm text-surface-200 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Subscription" subtitle="Manage your plan, billing, and referrals" />

      {/* ── Toast Banners ── */}
      {actionSuccess && (
        <div className="max-w-4xl mx-auto px-4 mb-4">
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center gap-3">
            <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-green-400 text-sm">{actionSuccess}</p>
          </div>
        </div>
      )}
      {actionError && (
        <div className="max-w-4xl mx-auto px-4 mb-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-3">
            <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-400 text-sm">{actionError}</p>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 pb-10 space-y-6">

        {/* ══════════════════════════════════════════════════════════════════
            STATUS-SPECIFIC HERO CARD
            ══════════════════════════════════════════════════════════════════ */}

        {/* ── No Subscription: Subscribe CTA ── */}
        {!hasSubscription && !status?.is_admin && !status?.is_comped && (
          <div className="bg-gradient-to-br from-primary-500/10 via-surface-800/50 to-surface-800/50 border border-primary-500/20 rounded-xl p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-surface-100 mb-2">Get Started with TradeCraft</h2>
                <p className="text-surface-400 mb-4">
                  Unlock automated trading, backtesting, and portfolio management with our annual subscription.
                </p>
                {plans.length > 0 && (
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-3xl font-bold text-primary-400">{plans[0].price_formatted}</span>
                    <span className="text-surface-400">/ {plans[0].billing_period}</span>
                  </div>
                )}
                {plans[0]?.features && plans[0].features.length > 0 && (
                  <ul className="space-y-2 mb-6">
                    {plans[0].features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-surface-300">
                        <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                )}
                {/* Active promotion banner */}
                {activePromotions.length > 0 && (
                  <div className="mb-4 p-3 rounded-lg bg-primary-500/10 border border-primary-500/30">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span className="text-sm font-medium text-primary-400">Limited Time Offer!</span>
                    </div>
                    {activePromotions.map((promo) => (
                      <div key={promo.id} className="text-sm text-surface-300">
                        Use code <span className="font-mono font-bold text-primary-300">{promo.code}</span> for <span className="text-primary-300 font-semibold">{promo.discount_percent}% off</span> your first year
                        {promo.description && <span className="text-surface-400"> — {promo.description}</span>}
                        <div className="text-xs text-surface-500 mt-0.5">
                          Expires {new Date(promo.ends_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Optional referral or promo code */}
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="text"
                    placeholder="Referral code (optional)"
                    value={referralInput}
                    onChange={(e) => { setReferralInput(e.target.value.toUpperCase()); if (e.target.value) setPromoInput(""); }}
                    className="w-48 px-3 py-2 bg-surface-700/60 border border-surface-600 rounded-lg text-sm text-surface-200 placeholder:text-surface-500 focus:outline-none focus:border-primary-500/50"
                    disabled={!!promoInput}
                  />
                  <span className="text-xs text-surface-500">or</span>
                  <input
                    type="text"
                    placeholder="Promo code (optional)"
                    value={promoInput}
                    onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); if (e.target.value) setReferralInput(""); }}
                    className="w-48 px-3 py-2 bg-surface-700/60 border border-surface-600 rounded-lg text-sm text-surface-200 placeholder:text-surface-500 focus:outline-none focus:border-primary-500/50"
                    disabled={!!referralInput}
                  />
                </div>
              </div>
              <div className="flex flex-col items-center gap-3">
                <button
                  onClick={handleSubscribe}
                  disabled={actionLoading || signingInProgress}
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-400 text-white font-semibold rounded-xl text-base shadow-lg shadow-primary-500/20 transition-all disabled:opacity-50"
                >
                  {actionLoading || signingInProgress ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {signingInProgress ? "Processing..." : "Redirecting..."}
                    </span>
                  ) : (
                    "Subscribe Now"
                  )}
                </button>
                <p className="text-xs text-surface-500">Secure checkout powered by Stripe</p>
              </div>
            </div>
          </div>
        )}

        {/* ── Active Subscription Dashboard ── */}
        {hasSubscription && isActive && (
          <div className="bg-surface-800/50 border border-surface-700/50 rounded-xl p-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-lg font-semibold text-surface-100">Your Subscription</h2>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeClasses(
                      status?.status ?? "none"
                    )}`}
                  >
                    {getStatusLabel(status?.status ?? "none")}
                  </span>
                </div>

                {status?.is_admin && (
                  <p className="text-xs text-purple-400 mb-2">Admin — full platform access</p>
                )}
                {status?.is_comped && (
                  <p className="text-xs text-purple-400 mb-2">Complimentary access granted</p>
                )}

                {currentPlan && (
                  <p className="text-sm text-surface-400">
                    {currentPlan.display_name} — {subscription?.amount_paid_display || currentPlan.price_formatted}/{currentPlan.billing_period}
                    {subscription?.amount_paid_cents && subscription.amount_paid_cents < currentPlan.price_cents && (
                      <span className="ml-2 text-green-400 text-xs">(discount applied)</span>
                    )}
                  </p>
                )}

                {/* Next payment / renewal info */}
                {renewalInfo && (
                  <div className="mt-4 bg-surface-700/30 border border-surface-700/50 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        renewalInfo.isExpiring
                          ? "bg-yellow-500/15 text-yellow-400"
                          : "bg-green-500/15 text-green-400"
                      }`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-surface-200">
                          {renewalInfo.isExpiring ? "Subscription Expires" : "Next Payment"}
                        </p>
                        <p className="text-xs text-surface-400">
                          {renewalInfo.date}
                          {renewalInfo.days > 0 && (
                            <span className={`ml-2 ${
                              renewalInfo.isExpiring && renewalInfo.days <= 7
                                ? "text-yellow-400 font-medium"
                                : "text-surface-500"
                            }`}>
                              ({renewalInfo.days} day{renewalInfo.days !== 1 ? "s" : ""} {renewalInfo.isExpiring ? "left" : "away"})
                            </span>
                          )}
                        </p>
                        {!renewalInfo.isExpiring && currentPlan && (
                          <p className="text-xs text-surface-500 mt-0.5">
                            Auto-renews at {currentPlan.price_formatted}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Grace period warning */}
                {status?.in_grace_period && status.grace_period_ends_at && (
                  <div className="mt-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-3">
                    <svg className="w-5 h-5 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-yellow-400">Payment Issue — Grace Period Active</p>
                      <p className="text-xs text-surface-400">
                        Access continues until {formatDate(status.grace_period_ends_at)}. Please update your payment method.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-2 min-w-[160px]">
                <button
                  onClick={handleManageBilling}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-surface-700 hover:bg-surface-600 border border-surface-600 text-surface-200 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Manage Billing
                </button>

                {isActiveSubscription(subscription.status) && !subscription.cancel_at_period_end && (
                  <button
                    onClick={() => setShowCancelDialog(true)}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-surface-700/50 hover:bg-red-500/10 border border-surface-700 hover:border-red-500/30 text-surface-400 hover:text-red-400 rounded-lg text-sm transition-all"
                  >
                    Cancel Subscription
                  </button>
                )}

                {subscription.cancel_at_period_end && (
                  <button
                    onClick={handleReactivate}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-primary-500/20 hover:bg-primary-500/30 border border-primary-500/30 text-primary-400 rounded-lg text-sm font-medium transition-all"
                  >
                    {actionLoading ? "Reactivating..." : "Reactivate"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Expiring Soon Warning ── */}
        {hasSubscription && subscription.cancel_at_period_end && renewalInfo && renewalInfo.days <= 14 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-400">
                  Your subscription expires in {renewalInfo.days} day{renewalInfo.days !== 1 ? "s" : ""}
                </p>
                <p className="text-xs text-surface-400 mt-1">
                  After {renewalInfo.date}, you'll lose access to trading, backtesting, and portfolio features.
                  Reactivate now to keep uninterrupted access.
                </p>
              </div>
              <button
                onClick={handleReactivate}
                disabled={actionLoading}
                className="px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 text-yellow-400 rounded-lg text-sm font-medium whitespace-nowrap transition-all"
              >
                Reactivate Now
              </button>
            </div>
          </div>
        )}

        {/* ── Payment Failed Banner ── */}
        {status?.status === "payment_failed" && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-400">Payment Failed</p>
                <p className="text-xs text-surface-400 mt-1">
                  Your last payment didn't go through. Please update your payment method to avoid service interruption.
                  {status.in_grace_period && status.grace_period_ends_at && (
                    <span className="text-yellow-400"> Grace period ends {formatDate(status.grace_period_ends_at)}.</span>
                  )}
                </p>
              </div>
              <button
                onClick={handleManageBilling}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 rounded-lg text-sm font-medium whitespace-nowrap transition-all"
              >
                Update Payment
              </button>
            </div>
          </div>
        )}

        {/* ── No Access (non-subscribed, non-admin) ── */}
        {!isActive && !hasSubscription && !status?.is_admin && !loading && (
          <div className="bg-surface-800/30 border border-surface-700/30 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-surface-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-surface-300">Trading Access Locked</p>
                <p className="text-xs text-surface-500 mt-1">
                  Subscribe above to unlock brokerage connections, automated trading, backtesting, and full portfolio management.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            SUBSCRIBER BENEFITS
            ══════════════════════════════════════════════════════════════════ */}
        <div className="bg-surface-800/40 border border-surface-700/40 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-surface-100 mb-1">What You Get</h3>
          <p className="text-xs text-surface-500 mb-5">
            Tools and data for your own independent research — TradeCraft is a platform, not a financial advisor.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              {
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                title: "Unlimited Backtesting",
                desc: "Run unlimited backtests 24/7 for the duration of your subscription",
              },
              {
                icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
                title: "Community Strategies",
                desc: "Unlimited access to community-shared strategies for research and learning",
              },
              {
                icon: "M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z",
                title: "Strategy Builder",
                desc: "Build and research your own strategies with all NYSE-listed stocks",
              },
              {
                icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                title: "Real-Time Market Data",
                desc: "Live pricing data to better evaluate your backtest runs and research",
              },
              {
                icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
                title: "Portfolio Performance",
                desc: "Daily performance snapshots so you can compare your results against the SPY benchmark",
              },
              {
                icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
                title: "Paper & Live Evaluation",
                desc: "Connect your own Alpaca paper or live account to test and evaluate your strategies independently",
              },
              {
                icon: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
                title: "Automated Execution",
                desc: "Run your strategies automatically in your Alpaca account to validate your own backtests and research",
              },
              {
                icon: "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4",
                title: "Full Strategy Control",
                desc: "Add funds, remove funds, cancel, or liquidate your own strategy allocations at any time",
              },
              {
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                title: "Backtest vs. Live Comparison",
                desc: "Compare how your strategies actually perform relative to your backtested expectations",
              },
              {
                icon: "M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0z",
                title: "Brokerage Performance",
                desc: "View your Alpaca paper and live account performance directly in the platform",
              },
              {
                icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
                title: "Live Strategy Updates",
                desc: "Real-time performance updates on your running strategies, visible right in the UI",
              },
            ].map((benefit) => (
              <div key={benefit.title} className="flex items-start gap-3 p-3 rounded-lg bg-surface-700/20 border border-surface-700/30">
                <div className="w-9 h-9 rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4.5 h-4.5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={benefit.icon} />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-surface-200">{benefit.title}</p>
                  <p className="text-xs text-surface-400 mt-0.5 leading-relaxed">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-surface-600 mt-5 leading-relaxed">
            TradeCraft is a self-directed research and execution platform. Nothing on this platform constitutes financial advice, investment recommendations, or solicitation to buy or sell securities. All trading involves risk — past performance does not guarantee future results. You are solely responsible for your own investment decisions.
          </p>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            TAB NAVIGATION
            ══════════════════════════════════════════════════════════════════ */}
        <div className="flex gap-1 bg-surface-800/30 p-1 rounded-lg border border-surface-700/30">
          {(["overview", "billing", "referrals"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === tab
                  ? "bg-surface-700 text-surface-100 shadow-sm"
                  : "text-surface-400 hover:text-surface-200"
              }`}
            >
              {tab === "billing" ? "Billing & Payments" : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            TAB CONTENT
            ══════════════════════════════════════════════════════════════════ */}

        {/* ── Overview Tab ── */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Plan details (for subscribed users) */}
            {hasSubscription && currentPlan && (
              <div>
                <h3 className="text-base font-semibold text-surface-200 mb-3">Plan Details</h3>
                <div className="bg-surface-800/50 border border-surface-700/50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-surface-100">{currentPlan.display_name}</h4>
                      {currentPlan.description && (
                        <p className="text-sm text-surface-400 mt-1">{currentPlan.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary-400">{currentPlan.price_formatted}</p>
                      <p className="text-xs text-surface-500">per {currentPlan.billing_period}</p>
                    </div>
                  </div>
                  {currentPlan.features.length > 0 && (
                    <div className="border-t border-surface-700/50 pt-3">
                      <p className="text-xs text-surface-500 font-medium mb-2">Includes</p>
                      <div className="grid grid-cols-2 gap-2">
                        {currentPlan.features.map((f) => (
                          <div key={f} className="flex items-center gap-2 text-sm text-surface-300">
                            <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {f}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Available plans for non-subscribers */}
            {!hasSubscription && plans.length > 1 && (
              <div>
                <h3 className="text-base font-semibold text-surface-200 mb-3">Available Plans</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      className="bg-surface-800/50 border border-surface-700/50 rounded-xl p-5 hover:border-primary-500/30 transition-colors"
                    >
                      <h4 className="font-semibold text-surface-100">{plan.display_name}</h4>
                      <p className="text-2xl font-bold text-primary-400 mt-1">
                        {plan.price_formatted}
                        <span className="text-sm font-normal text-surface-400">/{plan.billing_period}</span>
                      </p>
                      {plan.description && (
                        <p className="text-sm text-surface-400 mt-2">{plan.description}</p>
                      )}
                      {plan.features.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {plan.features.map((f) => (
                            <li key={f} className="flex items-center gap-2 text-sm text-surface-300">
                              <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              {f}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Methods */}
            {hasSubscription && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-surface-200">Payment Methods</h3>
                  <button
                    onClick={handleManageBilling}
                    disabled={actionLoading}
                    className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                  >
                    Manage in Stripe →
                  </button>
                </div>
                {paymentMethods.length === 0 ? (
                  <div className="bg-surface-800/30 border border-surface-700/30 rounded-xl p-6 text-center">
                    <p className="text-surface-400 text-sm">No payment methods on file.</p>
                    <button
                      onClick={handleManageBilling}
                      className="mt-3 text-sm text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      Add a payment method →
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {paymentMethods.map((method) => (
                      <div
                        key={method.id}
                        className="bg-surface-800/50 border border-surface-700/50 rounded-lg p-4 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-7 bg-surface-700 rounded flex items-center justify-center">
                            <span className="text-xs font-mono text-surface-300 uppercase">
                              {method.card_brand || method.type}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm text-surface-200">
                              {method.card_brand ? `${method.card_brand} ` : ""}
                              •••• {method.card_last4 || method.bank_last4 || "****"}
                            </p>
                            {method.card_expiry && (
                              <p className="text-xs text-surface-500">Exp: {method.card_expiry}</p>
                            )}
                          </div>
                          {method.is_default && (
                            <span className="px-2 py-0.5 bg-primary-500/15 text-primary-400 rounded text-xs font-medium">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {!method.is_default && (
                            <button
                              onClick={() => handleSetDefaultPaymentMethod(method.id)}
                              disabled={actionLoading}
                              className="text-xs text-surface-400 hover:text-primary-400 transition-colors"
                            >
                              Set Default
                            </button>
                          )}
                          <button
                            onClick={() => handleDeletePaymentMethod(method.id)}
                            disabled={actionLoading}
                            className="text-xs text-surface-400 hover:text-red-400 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Billing & Payments Tab ── */}
        {activeTab === "billing" && (
          <div className="space-y-6">
            {/* Quick Actions */}
            {hasSubscription && (
              <div className="flex gap-3">
                <button
                  onClick={handleManageBilling}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-3 bg-surface-800/50 hover:bg-surface-700/50 border border-surface-700/50 rounded-xl text-sm text-surface-200 font-medium transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Update Payment Method
                </button>
                <button
                  onClick={handleManageBilling}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-3 bg-surface-800/50 hover:bg-surface-700/50 border border-surface-700/50 rounded-xl text-sm text-surface-200 font-medium transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  View Invoices
                </button>
              </div>
            )}

            {/* Payment History */}
            <div>
              <h3 className="text-base font-semibold text-surface-200 mb-3">Payment History</h3>
              {paymentHistory.length === 0 ? (
                <div className="bg-surface-800/30 border border-surface-700/30 rounded-xl p-6 text-center">
                  <p className="text-surface-400 text-sm">No payment history yet.</p>
                </div>
              ) : (
                <div className="bg-surface-800/50 border border-surface-700/50 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-surface-700/50">
                        <th className="text-left py-3 px-4 text-surface-400 font-medium">Date</th>
                        <th className="text-left py-3 px-4 text-surface-400 font-medium">Amount</th>
                        <th className="text-left py-3 px-4 text-surface-400 font-medium">Type</th>
                        <th className="text-left py-3 px-4 text-surface-400 font-medium">Status</th>
                        <th className="text-right py-3 px-4 text-surface-400 font-medium">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map((item) => (
                        <tr key={item.id} className="border-b border-surface-700/30 last:border-0">
                          <td className="py-3 px-4 text-surface-300">
                            {new Date(item.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 text-surface-200 font-medium">
                            {item.amount_display}
                          </td>
                          <td className="py-3 px-4 text-surface-400 capitalize">{item.type}</td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                item.status === "succeeded"
                                  ? "bg-green-500/15 text-green-400"
                                  : item.status === "failed"
                                  ? "bg-red-500/15 text-red-400"
                                  : item.status === "refunded"
                                  ? "bg-yellow-500/15 text-yellow-400"
                                  : "bg-surface-600/50 text-surface-400"
                              }`}
                            >
                              {item.status}
                            </span>
                            {item.failure_message && (
                              <p className="text-xs text-red-400/70 mt-1">{item.failure_message}</p>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {item.receipt_url && (
                                <a
                                  href={item.receipt_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                                >
                                  Receipt
                                </a>
                              )}
                              {item.invoice_pdf_url && (
                                <a
                                  href={item.invoice_pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                                >
                                  Invoice
                                </a>
                              )}
                              {!item.receipt_url && !item.invoice_pdf_url && (
                                <span className="text-xs text-surface-600">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Referrals Tab ── */}
        {activeTab === "referrals" && (
          <div className="space-y-6">
            {/* Referral Share Card */}
            <div>
              <h3 className="text-base font-semibold text-surface-200 mb-3">Refer a Friend</h3>
              <div className="bg-gradient-to-br from-primary-500/5 via-surface-800/50 to-surface-800/50 border border-primary-500/15 rounded-xl p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-primary-500/15 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-surface-200">Share TradeCraft, Earn Rewards</p>
                    <p className="text-xs text-surface-400 mt-1">
                      Give your friends {referralCode?.discount_percent || 5}% off their first subscription.
                      When they subscribe, you earn referral credits toward your next renewal.
                    </p>
                  </div>
                </div>

                {referralCode ? (
                  <div className="space-y-3">
                    {/* Referral Code */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-surface-700/40 border border-surface-600/50 rounded-lg px-4 py-2.5 flex items-center justify-between">
                        <code className="text-base font-mono font-bold text-primary-400">{referralCode.code}</code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(referralCode.code);
                            setActionSuccess("Referral code copied!");
                          }}
                          className="px-3 py-1 bg-surface-600/50 hover:bg-surface-600 rounded text-xs text-surface-300 transition-colors"
                        >
                          Copy Code
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <div className="flex gap-4 text-xs text-surface-400">
                        <span>Discount: {referralCode.discount_percent}%</span>
                        <span>
                          Used: {referralCode.times_used}
                          {referralCode.max_uses && referralCode.max_uses > 0 ? ` / ${referralCode.max_uses}` : ""}
                        </span>
                        <span className={referralCode.is_active ? "text-green-400" : "text-red-400"}>
                          {referralCode.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <button
                        onClick={handleRegenerateReferralCode}
                        disabled={actionLoading}
                        className="text-xs text-surface-500 hover:text-surface-300 transition-colors"
                      >
                        Regenerate Code
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-surface-400 text-sm">Loading referral code...</p>
                )}
              </div>
            </div>

            {/* Referral Credits */}
            <div>
              <h3 className="text-base font-semibold text-surface-200 mb-3">Your Referral Credits</h3>
              <div className="bg-surface-800/50 border border-surface-700/50 rounded-xl p-5">
                {referralCredits ? (
                  <>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center bg-surface-700/30 rounded-lg py-3">
                        <p className="text-2xl font-bold text-surface-100">{referralCredits.total_referrals}</p>
                        <p className="text-xs text-surface-400">Total Referrals</p>
                      </div>
                      <div className="text-center bg-surface-700/30 rounded-lg py-3">
                        <p className="text-2xl font-bold text-yellow-400">{referralCredits.pending_credits}</p>
                        <p className="text-xs text-surface-400">Pending</p>
                      </div>
                      <div className="text-center bg-surface-700/30 rounded-lg py-3">
                        <p className="text-2xl font-bold text-green-400">{referralCredits.applied_credits}</p>
                        <p className="text-xs text-surface-400">Applied</p>
                      </div>
                    </div>
                    {referralCredits.referrals.length > 0 && (
                      <div className="border-t border-surface-700/50 pt-3">
                        <p className="text-xs text-surface-500 mb-2 font-medium">Recent Referrals</p>
                        {referralCredits.referrals.map((ref, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between py-2 border-b border-surface-700/20 last:border-0"
                          >
                            <span className="text-sm text-surface-300">{ref.referred_email || "—"}</span>
                            <div className="flex items-center gap-3">
                              {ref.credit_amount_cents && (
                                <span className="text-xs text-green-400">
                                  {formatCents(ref.credit_amount_cents)}
                                </span>
                              )}
                              <span
                                className={`text-xs px-1.5 py-0.5 rounded ${
                                  ref.status === "credited"
                                    ? "bg-green-500/15 text-green-400"
                                    : ref.status === "activated"
                                    ? "bg-blue-500/15 text-blue-400"
                                    : "bg-surface-600/50 text-surface-400"
                                }`}
                              >
                                {ref.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-surface-400 text-sm">No referral data available.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        title="Cancel Subscription"
        message={`Your subscription will remain active until ${
          renewalInfo?.date || "the end of the current billing period"
        }. You can reactivate at any time before it expires. After that, you'll lose access to trading and portfolio features.`}
        confirmText="Cancel Subscription"
        onConfirm={handleCancel}
        onClose={() => setShowCancelDialog(false)}
        variant="danger"
        isLoading={actionLoading}
      />
    </div>
  );
}
